import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import TimeSlotDto from "../../dtos/Partner/timeSlotDto";
import {
  CookSlotTimeEnum,
  CookSlotTimeEnumArray,
} from "../../enums/CookSlotTimeEnum";
import { PartnerTimeSlotStatusEnums } from "../../enums/partnerTimeSlotStatusEnums";
import PartnerSectorService from "../../services/PartnerSectorService";
import PartnerSlotService from "../../services/PartnerTimeSlotService";
import { useLocation } from "react-router-dom";
import { formatDateTimeWithSeconds } from "../../utils/DateTimeUtils";
import "./styles.css";
import { CookService } from "../../services/CookServices";
import { EventBusEventsEnum } from "../../enums/EventBusEventsEnum";
import EventBus from "../../EventBus";
import { CustomSnackbarTypes } from "../Common/CustomSnackBar";
import { useRecordContext, useRefresh } from "react-admin";
import { useAppSelector } from "../../store/hooks";
import GroupPermissionFunction from "../../utils/groupPermissionFunction";
import { UserGroupsEnum } from "../../enums/UserGroupsEnum";
import CommonListTable, { CommonListColumn } from "../Common/CommonListTable";

const partnerSlotService = new PartnerSlotService();
const partnerSectorService = new PartnerSectorService();
const cookService = new CookService();

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const constructStaticSlotMap = (): Map<
  CookSlotTimeEnum,
  {
    slotName: CookSlotTimeEnum;
    status: PartnerTimeSlotStatusEnums;
    color: string;
    trialBlockedAt: Date | null;
    lastSlotsUpdatedByAdminAt: Date | null;
    lastSlotsUpdatedByAppAt: Date | null;
  }
> => {
  const slotMap = new Map<CookSlotTimeEnum, any>();
  for (const timeEnumkey of CookSlotTimeEnumArray) {
    slotMap.set(timeEnumkey, {
      slotName: timeEnumkey,
      status: PartnerTimeSlotStatusEnums.BLOCKED,
      color: "#ffdede",
      trialBlockedAt: null,
      lastSlotsUpdatedByAdminAt: null,
      lastSlotsUpdatedByAppAt: null,
    });
  }
  return slotMap;
};

const getColor = (status: String): string => {
  if (status === "FREE") return "#e6ffe6";
  if (status === "BLOCKED") return "#ffdede";
  return "#fffff2";
};

const CookTimeSlotList = (props: any) => {
  const userDetails = useAppSelector((state) => state.userDetails.value);
  let groupsArray: string[] = userDetails.adminUser.accessGroups;
  const [updateSectorModal, setUpdateSectorModal] = useState<boolean>(false);
  const [serviceSector, setServiceSector] = useState<number[]>([0]);
  const [slots, setSlots] = useState<
    Map<
      CookSlotTimeEnum,
      {
        slotName: CookSlotTimeEnum;
        status: PartnerTimeSlotStatusEnums;
        color: string;
        trialBlockedAt: Date | null;
        lastSlotsUpdatedByAdminAt: Date | null;
        lastSlotsUpdatedByAppAt: Date | null;
      }
    >
  >(constructStaticSlotMap());
  const [isDataAvailable, setDataAvailable] = useState<boolean>(false);
  const [dataChange, setDataChange] = useState<boolean>(false);
  const location = useLocation();
  const cookId = location.pathname.split("/")[2];

  let record = useRecordContext();
  const refresh = useRefresh();

  const getSlots = useCallback(async (cookId: number) => {
    setDataChange(false);
    const response = await partnerSlotService.getCookSlots(cookId);
    let slotMap = constructStaticSlotMap();
    response.forEach((element) => {
      let val = slotMap.get(element.timeSlot as any as CookSlotTimeEnum);
      if (val) {
        val.slotName = element.timeSlot as any as CookSlotTimeEnum;
        val.trialBlockedAt = element.trialBlockedAt;
        val.status = element.status;
        val.color = getColor(element.status);
        val.lastSlotsUpdatedByAdminAt = element.lastSlotsUpdatedByAdminAt;
        val.lastSlotsUpdatedByAppAt = element.lastSlotsUpdatedByAppAt;
      }
    });
    setSlots(slotMap);
  }, []);

  useEffect(() => {
    getSlots(props.cookId);
    setDataAvailable(true);
  }, [props.cookId, dataChange, getSlots]);

  const handleSectorChange = (e: any) => {
    setServiceSector(e.target.value);
  };

  const handleCloseSectorModal = () => {
    setDataChange(true);
    setUpdateSectorModal(false);
  };

  const requestForWork = async () => {
    try {
      await cookService.requestedForWorkCook(Number(props.cookId));
      EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
        message: "Success",
        type: CustomSnackbarTypes.SUCCESS,
      });
      refresh();
    } catch (error) {
      console.error(error);
    }
    return;
  };

  const recalculateV1 = async () => {
    try {
      await cookService.recalculateV1(Number(props.cookId));
      EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
        message: "Success",
        type: CustomSnackbarTypes.SUCCESS,
      });
      refresh();
    } catch (error) {
      EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
        message: "Error",
        type: CustomSnackbarTypes.ERROR,
      });
    }
  };

  const recalculateV2 = async () => {
    try {
      await cookService.recalculateV2(Number(props.cookId));
      EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
        message: "Success",
        type: CustomSnackbarTypes.SUCCESS,
      });
      refresh();
    } catch (error) {
      EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
        message: "Error",
        type: CustomSnackbarTypes.ERROR,
      });
    }
  };

  const slotData = isDataAvailable ? Array.from(slots.entries()).map(([, value]) => value) : [];

  const columns: CommonListColumn[] = [
    { label: 'Time Slot', render: (row) => row?.slotName },
    {
      label: 'Status',
      render: (row) => (
        <Chip
          label={row?.status}
          size="small"
          sx={{ backgroundColor: row?.color, color: '#263238', fontWeight: 500 }}
        />
      ),
    },
    {
      label: 'Last Slots Updated By Admin At',
      nowrap: true,
      render: (row) => row?.lastSlotsUpdatedByAdminAt ? formatDateTimeWithSeconds(row?.lastSlotsUpdatedByAdminAt) : "",
    },
    {
      label: 'Last Slots Updated By App At',
      nowrap: true,
      render: (row) => row?.lastSlotsUpdatedByAppAt ? formatDateTimeWithSeconds(row?.lastSlotsUpdatedByAppAt) : "",
    },
    {
      label: 'Update Cook Slot',
      stopRowClick: true,
      render: (row) => {
        if (row?.status === PartnerTimeSlotStatusEnums.CK_BLOCKED) return null;
        return (
          <>
            <Button
              variant="contained"
              style={{ marginRight: "10px" }}
              onClick={() => {
                const timeSlotDto: TimeSlotDto = {
                  timeSlot: row?.slotName,
                  status: PartnerTimeSlotStatusEnums.FREE,
                };
                partnerSlotService.postSlotsForCook(Number(cookId), [timeSlotDto]).then(() => {
                  setDataChange(true);
                });
              }}
              size={"medium"}
            >
              Free
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                const timeSlotDto: TimeSlotDto = {
                  timeSlot: row?.slotName,
                  status: PartnerTimeSlotStatusEnums.BLOCKED,
                };
                partnerSlotService.postSlotsForCook(Number(cookId), [timeSlotDto]).then(() => {
                  setDataChange(true);
                });
              }}
              size={"medium"}
            >
              Blocked
            </Button>
          </>
        );
      },
    },
  ];

  return (
    <div>
      <Dialog open={updateSectorModal} scroll={"paper"} maxWidth="xl" onBackdropClick={handleCloseSectorModal}>
        <DialogTitle>Update Sector</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} justifyContent={"space-evenly"} style={{ padding: "12px 12px 12px 12px" }}>
            <Grid item>
              <FormControl style={{ minWidth: 120 }} fullWidth>
                <InputLabel id="demo-multiple-name-label">Sectors</InputLabel>
                <Select
                  autoWidth={true}
                  labelId="demo-multiple-name-label"
                  id="demo-multiple-name"
                  multiple
                  value={serviceSector}
                  onChange={handleSectorChange}
                  input={<OutlinedInput label="Sectors" />}
                  MenuProps={MenuProps}
                >
                  {[...Array(200)].map((x, i) => (
                    <MenuItem key={i + 1} value={i + 1}>{i + 1}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions style={{ marginTop: "12px" }}>
          <Button
            variant="contained"
            onClick={() => {
              partnerSectorService.postCookSector(props.cookId, serviceSector).then(() => {
                setDataChange(true);
                setUpdateSectorModal(false);
              });
            }}
            size={"large"}
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>

      <Box display="flex" justifyContent="flex-end" alignItems="center" flexWrap="wrap" gap={1} mb={2}>
        <Button
          variant="contained"
          size="small"
          onClick={() => recalculateV1()}
          disabled={!GroupPermissionFunction(groupsArray, [UserGroupsEnum.ADMIN])}
        >
          Recalculate V1 slot
        </Button>
        <Button
          variant="contained"
          size="small"
          onClick={() => recalculateV2()}
          disabled={!GroupPermissionFunction(groupsArray, [UserGroupsEnum.ADMIN])}
        >
          Recalculate V2 slot
        </Button>
        {record.workRequestedAt
          ? 'Already requested for work'
          : (
            <Button variant="contained" size="small" onClick={requestForWork}>
              Requested for Work
            </Button>
          )
        }
      </Box>

      <CommonListTable
        columns={columns}
        data={slotData}
        loading={!isDataAvailable}
        rowKey={(row) => row?.slotName}
        emptyText="No slots found"
      />
    </div>
  );
};

export default CookTimeSlotList;
